// CSR-953 — read-only Physical Damage card rendered once per deductible tier
// when a policy carries more than one distinct (comp, coll) tier. Per the
// resolved OQ-2 (option a), tier cards are READ-ONLY: no Edit/Remove controls
// and nothing writes back to DC. Single-tier / fallback policies keep the
// existing editable `CoverageRow` (see `PolicyCoveragesTab`).

import { formatDollarOrDash } from "@/lib/policy-coverages";
import type { PhysicalDamageTier } from "@shared/physical-damage-tiers";
import { VehicleSection } from "./PhysicalDamageVehicleSection";
import {
  CoverageStatusBadge,
  type CoverageBadgeStatus,
} from "./CoverageStatusBadge";

interface Props {
  tier: PhysicalDamageTier;
  // CSR-1978 — resolved by the parent from the Physical Damage line + the
  // policy's own status, so a tier card can't claim "Active" coverage on a
  // policy that is no longer in force.
  status: CoverageBadgeStatus;
}

export function PhysicalDamageTierCard({ tier, status }: Props) {
  const comp = formatDollarOrDash(tier.comprehensive);
  const coll = formatDollarOrDash(tier.collision);
  const testId = `physical-damage-tier-card-${tier.comprehensive ?? "none"}-${tier.collision ?? "none"}`;
  const rowKey = `${tier.comprehensive ?? "none"}-${tier.collision ?? "none"}`;

  return (
    <div
      className="border border-border rounded-xl p-5 bg-card"
      data-testid={testId}
      data-coverage-status={status}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-foreground">Physical Damage</p>
          <CoverageStatusBadge status={status} />
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
        <Metric label="Comprehensive Deductible" value={comp} />
        <Metric label="Collision Deductible" value={coll} />
      </dl>
      <div className="mt-3 space-y-4 pt-3 border-t border-border">
        <VehicleSection
          title="POWER UNITS"
          rowLabel="Power Unit"
          vehicles={tier.powerUnits}
          total={tier.powerUnits.length}
          testId={`physical-damage-tier-power-units-${rowKey}`}
          rowTestIdPrefix={`physical-damage-tier-row-${rowKey}`}
        />
        <VehicleSection
          title="TRAILERS"
          rowLabel="Trailer"
          vehicles={tier.trailers}
          total={tier.trailers.length}
          testId={`physical-damage-tier-trailers-${rowKey}`}
          rowTestIdPrefix={`physical-damage-tier-row-${rowKey}`}
        />
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
      <dd className="font-semibold text-foreground">{value}</dd>
    </div>
  );
}
